import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMarketerDashboard, getMarketerLocations } from '../api/marketer';

// Self-contained, read-only dashboard for Skipzy Representatives (marketers).
// access_level='marketer' sees ONLY this — no other ZOCS nav or screens.

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'last_2_days', label: 'Last 2 days' },
  { key: 'last_10_days', label: 'Last 10 days' },
  { key: 'current_weekend', label: 'This weekend' },
  { key: 'last_weekend', label: 'Last weekend' },
  { key: 'last_month', label: 'Last month' },
  { key: 'lifetime', label: 'Lifetime' },
  { key: 'custom', label: 'Custom' },
];

// Human phrase for the message body per period (reads after "over ...").
const MSG_PERIOD = {
  today: 'today', last_2_days: 'the last 2 days', last_10_days: 'the last 10 days',
  current_weekend: 'this weekend', last_weekend: 'last weekend',
  last_month: 'the last month', lifetime: 'all time',
};

export default function MarketerApp() {
  const { auth, logout } = useAuth();
  const [view, setView] = useState('promoters'); // 'promoters' | 'locations'
  const [period, setPeriod] = useState('last_10_days');
  const [sort, setSort] = useState('orders');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedName, setCopiedName] = useState(null);

  const fetchFn = view === 'locations' ? getMarketerLocations : getMarketerDashboard;
  const load = useCallback(async () => {
    if (period === 'custom' && (!dateFrom || !dateTo)) return;
    setLoading(true); setError(null);
    try { setData(await fetchFn({ period, sort, dateFrom, dateTo })); }
    catch (e) { setError(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [view, period, sort, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];

  function buildMsg(row) {
    const p = data?.period || period;   // use the loaded period so all messages match the shown data
    const phrase = p === 'custom'
      ? `the period ${data?.date_from} to ${data?.date_to}`
      : (MSG_PERIOD[p] || 'this period');
    const pct = row.completion_pct ?? 0;
    const maxAppts = Math.max(...rows.map(r => r.appointments));
    const maxPct = Math.max(...rows.map(r => r.completion_pct ?? 0));
    return `Hi! Wanted to let you know your performance over ${phrase} compared to others. You did ${row.appointments} appointments and have had a conversion rate of ${pct}%. The conversion rate is % of orders that actually happened/appointments you booked where the delivery date was till today.\n\nFor comparison, the highest number of appointments was ${maxAppts} and the highest conversion rate was ${maxPct}%.`;
  }

  async function copyMsg(row) {
    try {
      await navigator.clipboard.writeText(buildMsg(row));
      setCopiedName(row.name);
      setTimeout(() => setCopiedName(null), 2000);
    } catch (e) { alert('Could not copy to clipboard'); }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-800">{view === 'locations' ? 'Location View' : 'Skipzy Representatives'}</h1>
          <p className="text-xs text-gray-400">Read-only performance dashboard</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">{auth?.full_name || auth?.username}</span>
          <button onClick={logout} className="text-xs text-gray-400 hover:text-zoddle-pink transition">Log out</button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        {/* View toggle */}
        <div className="flex gap-2 mb-4">
          <button onClick={() => setView('promoters')}
            className={`text-sm font-semibold px-4 py-2 rounded-lg border transition ${view === 'promoters' ? 'bg-gray-700 text-white border-gray-700' : 'border-gray-200 text-gray-600'}`}>
            Promoters
          </button>
          <button onClick={() => setView('locations')}
            className={`text-sm font-semibold px-4 py-2 rounded-lg border transition ${view === 'locations' ? 'bg-gray-700 text-white border-gray-700' : 'border-gray-200 text-gray-600'}`}>
            Location View
          </button>
        </div>

        {/* Period selector */}
        <div className="flex flex-wrap gap-2 mb-3">
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition ${period === p.key ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
              {p.label}
            </button>
          ))}
        </div>
        {period === 'custom' && (
          <div className="flex items-center gap-2 mb-3 text-sm">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5" />
            <span className="text-gray-400">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5" />
          </div>
        )}

        {/* Sort + meta */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-400">Sort by</span>
            <button onClick={() => setSort('orders')}
              className={`px-2.5 py-1 rounded-lg border ${sort === 'orders' ? 'bg-gray-700 text-white border-gray-700' : 'border-gray-200 text-gray-600'}`}>Number of orders</button>
            <button onClick={() => setSort('conversion')}
              className={`px-2.5 py-1 rounded-lg border ${sort === 'conversion' ? 'bg-gray-700 text-white border-gray-700' : 'border-gray-200 text-gray-600'}`}>% conversion</button>
          </div>
          {data && (
            <span className="text-xs text-gray-400">
              {data.date_from ? `${data.date_from} → ${data.date_to}` : 'All time'} · {data.generated_at_ist} IST
            </span>
          )}
        </div>

        {/* Table */}
        {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
        : error ? <div className="text-center py-16 text-zoddle-pink text-sm">{error}</div>
        : rows.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm">No data for this period</div>
        : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs font-semibold text-gray-500">
                  <th className="px-4 py-3">{view === 'locations' ? 'Location' : 'Skipzy Representative'}</th>
                  <th className="px-4 py-3 text-right">Appointments</th>
                  <th className="px-4 py-3 text-right">Orders (slot passed)</th>
                  <th className="px-4 py-3 text-right">Completed</th>
                  <th className="px-4 py-3 text-right">AOV</th>
                  <th className="px-4 py-3 text-right">AOV Incl RTO</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.name} className={`border-b border-gray-100 ${i % 2 ? 'bg-gray-50/30' : ''}`}>
                    <td className="px-4 py-3 font-medium text-gray-800">{r.name}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{r.appointments}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{r.slot_passed}</td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {r.completed}
                      {r.completion_pct !== null && <span className="text-gray-400"> ({r.completion_pct}%)</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {r.aov !== null && r.aov !== undefined ? `₹${r.aov.toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {r.aov_incl_rto !== null && r.aov_incl_rto !== undefined ? `₹${r.aov_incl_rto.toLocaleString('en-IN')}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-gray-400 mt-3">
          Period filters by the date the appointment was taken. "Appointments" counts each customer once, and only if
          they had no other booking or order in the 10 days before this one (the first booking gets the credit).
          "Orders (slot passed)" = appointments whose time slot has ended, or that were cancelled / only messaged.
          "Completed" = finished or RTO, including if the same customer ordered again within 10 days (% of slot-passed).
          "AOV" = average value of actual sales only. "AOV Incl RTO" = same total divided across sales + RTOs (lower, since RTOs contribute ₹0).
          {view === 'locations' && ' "Location Unknown" = the intern who took the appointment has no roster record for that date.'}
        </p>

        {view === 'promoters' && rows.length > 0 && (
          <div className="mt-6 border-t border-gray-200 pt-4">
            <p className="text-xs font-semibold text-gray-500 mb-2">Copy performance message per rep</p>
            <div className="space-y-1.5">
              {rows.map(r => (
                <div key={r.name} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2">
                  <span className="text-sm text-gray-700">{r.name}</span>
                  <button onClick={() => copyMsg(r)}
                    className="text-xs font-semibold text-zoddle-blue hover:text-zoddle-green transition">
                    {copiedName === r.name ? 'Copied!' : 'Copy message'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
